import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

function ToolsDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    dmca: {
      totalCases: 0,
      todayCases: 0,
      malware: 0,
      phishing: 0,
      ncc: 0,
      spyApp: 0,
      recentMalware: 0,
      recentPhishing: 0,
      recentNcc: 0,
      recentSpyApp: 0
    },
    spamhaus: {
      totalSubmissions: 0,
      totalDomains: 0,
      successfulSubmissions: 0,
      todaySubmissions: 0
    },
    googleMalware: {
      totalReports: 0,
      pendingReports: 0,
      submittedReports: 0,
      todayReports: 0
    },
    malwareBazaar: {
      totalSubmissions: 0,
      todaySubmissions: 0,
      submitted: 0,
      failed: 0,
      alreadyKnown: 0
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      
      // Use environment variable for API base URL
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      
      // Fetch DMCA statistics using new statistics endpoint
      const dmcaResponse = await fetch(`${API_BASE_URL}/cases/statistics`);
      if (dmcaResponse.ok) {
        const dmcaData = await dmcaResponse.json();
        
        console.log('DMCA Statistics:', dmcaData); // Debug log
        
        const dmcaStats = {
          totalCases: dmcaData.summary?.totalCases || 0,
          todayCases: dmcaData.summary?.todayCases || 0,
          malware: dmcaData.byType?.malware || 0,
          phishing: dmcaData.byType?.phishing || 0,
          ncc: dmcaData.byType?.ncc || 0,
          spyApp: dmcaData.byType?.['spy-app'] || 0,
          recentMalware: dmcaData.recentByType?.malware || 0,
          recentPhishing: dmcaData.recentByType?.phishing || 0,
          recentNcc: dmcaData.recentByType?.ncc || 0,
          recentSpyApp: dmcaData.recentByType?.['spy-app'] || 0
        };
        
        setStats(prev => ({
          ...prev,
          dmca: dmcaStats
        }));
      } else {
        console.error('DMCA API Error:', dmcaResponse.status, dmcaResponse.statusText);
      }
      
      // Fetch Spamhaus statistics using simple domain counts API
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const spamhausResponse = await fetch(`${API_BASE_URL}/spamhaus/domain-counts`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (spamhausResponse.ok) {
            const spamhausData = await spamhausResponse.json();
            
            console.log('Spamhaus Domain Counts:', spamhausData); // Debug log
            
            setStats(prev => ({
              ...prev,
              spamhaus: {
                totalSubmissions: spamhausData.totalDomains || 0, // Total domains submitted
                totalDomains: spamhausData.domainsReported || 0, // Domains successfully reported  
                successfulSubmissions: spamhausData.successfulDomains || 0, // Successful domain submissions
                todaySubmissions: 0 // We'll add this later if needed
              }
            }));
          } else {
            console.error('Spamhaus API Error:', spamhausResponse.status, spamhausResponse.statusText);
          }
        } catch (error) {
          console.error("Error fetching Spamhaus domain counts:", error);
          // Keep default values if API fails
        }

        // Fetch Google Malware statistics
        try {
          const googleMalwareResponse = await fetch(`${API_BASE_URL}/google-malware/statistics`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (googleMalwareResponse.ok) {
            const googleMalwareData = await googleMalwareResponse.json();
            
            console.log('Google Malware Statistics:', googleMalwareData); // Debug log
            
            setStats(prev => ({
              ...prev,
              googleMalware: {
                totalReports: googleMalwareData.summary?.totalReports || 0,
                pendingReports: googleMalwareData.summary?.pendingReports || 0,
                submittedReports: googleMalwareData.summary?.submittedReports || 0,
                todayReports: googleMalwareData.summary?.todayReports || 0
              }
            }));
          } else {
            console.error('Google Malware API Error:', googleMalwareResponse.status, googleMalwareResponse.statusText);
          }
        } catch (error) {
          console.error("Error fetching Google Malware statistics:", error);
          // Keep default values if API fails
        }

        // Fetch Malware Bazaar statistics
        try {
          const malwareBazaarResponse = await fetch(`${API_BASE_URL}/malware-bazaar/statistics`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (malwareBazaarResponse.ok) {
            const malwareBazaarData = await malwareBazaarResponse.json();
            
            console.log('Malware Bazaar Statistics:', malwareBazaarData); // Debug log
            
            setStats(prev => ({
              ...prev,
              malwareBazaar: {
                totalSubmissions: malwareBazaarData.summary?.totalSubmissions || 0,
                todaySubmissions: malwareBazaarData.summary?.todaySubmissions || 0,
                submitted: malwareBazaarData.byStatus?.submitted || 0,
                failed: malwareBazaarData.byStatus?.failed || 0,
                alreadyKnown: malwareBazaarData.byStatus?.alreadyKnown || 0
              }
            }));
          } else {
            console.error('Malware Bazaar API Error:', malwareBazaarResponse.status, malwareBazaarResponse.statusText);
          }
        } catch (error) {
          console.error("Error fetching Malware Bazaar statistics:", error);
          // Keep default values if API fails
        }
      }
      
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/login");
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: "white", 
        padding: "1rem 2rem", 
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        borderBottom: "1px solid #e9ecef"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{ margin: 0, color: "#333" }}>Welcome to Digital Safe India Tools</h1>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666" }}>
              Select a tool below to get started with your cyber safety operations
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              padding: "0.5rem 1rem",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: "2rem" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* Tools Cards */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", 
            gap: "2rem",
            marginBottom: "2rem"
          }}>
            {/* DMCA Tool Card */}
            <div 
              style={{ 
                backgroundColor: "white", 
                borderRadius: "12px", 
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                overflow: "hidden",
                transition: "transform 0.2s ease",
                cursor: "pointer"
              }}
              onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
              onMouseLeave={(e) => e.target.style.transform = "translateY(0px)"}
            >
              <div style={{
                backgroundColor: "#007bff",
                color: "white",
                padding: "1.5rem",
                textAlign: "center"
              }}>
                <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>🛡️</div>
                <h2 style={{ margin: "0 0 0.5rem 0", fontSize: "1.5rem" }}>Takedown Tool</h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "0.9rem" }}>
                  Automated DMCA notice generation and delivery system
                </p>
              </div>
              <div style={{ padding: "1.5rem" }}>
                {loading ? (
                  <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
                    Loading statistics...
                  </div>
                ) : (
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "1fr 1fr", 
                    gap: "0.75rem" 
                  }}>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#007bff" }}>
                        {stats.dmca.totalCases}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Total Cases
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#28a745" }}>
                        {stats.dmca.malware}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Malware
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#17a2b8" }}>
                        {stats.dmca.phishing}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Phishing
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#ffc107" }}>
                        {stats.dmca.ncc}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        NCC
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px", gridColumn: "span 2" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#dc3545" }}>
                        {stats.dmca.spyApp}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Spy-App
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/new-case");
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "#007bff",
                      color: "white",
                      border: "none",
                      padding: "0.75rem",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      cursor: "pointer"
                    }}
                  >
                    🚨 Report Incident
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/dashboard");
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      padding: "0.75rem",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      cursor: "pointer"
                    }}
                  >
                    📊 View Dashboard
                  </button>
                </div>
              </div>
            </div>

            {/* Spamhaus Tool Card */}
            <div 
              style={{ 
                backgroundColor: "white", 
                borderRadius: "12px", 
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                overflow: "hidden",
                transition: "transform 0.2s ease",
                cursor: "pointer"
              }}
              onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
              onMouseLeave={(e) => e.target.style.transform = "translateY(0px)"}
            >
              <div style={{
                backgroundColor: "#28a745",
                color: "white",
                padding: "1.5rem",
                textAlign: "center"
              }}>
                <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>🛡️</div>
                <h2 style={{ margin: "0 0 0.5rem 0", fontSize: "1.5rem" }}>Spamhaus Domain Reporter</h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "0.9rem" }}>
                  Report malicious domains to Spamhaus Project for community protection
                </p>
              </div>
              <div style={{ padding: "1.5rem" }}>
                {loading ? (
                  <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
                    Loading statistics...
                  </div>
                ) : (
                  <div style={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    gap: "0.75rem" 
                  }}>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#28a745" }}>
                        {stats.spamhaus.totalSubmissions}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Total Domain Submissions
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#17a2b8" }}>
                        {stats.spamhaus.totalDomains}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Domains Reported
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#ffc107" }}>
                        {stats.spamhaus.successfulSubmissions}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Successful Domain Reported
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/spamhaus");
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      padding: "0.75rem",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      cursor: "pointer"
                    }}
                  >
                    🚨 Report Domains
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/spamhaus/history");
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      padding: "0.75rem",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      cursor: "pointer"
                    }}
                  >
                    📊 View Dashboard
                  </button>
                </div>
              </div>
            </div>

            {/* Google Malware Reporter Tool Card */}
            <div 
              style={{ 
                backgroundColor: "white", 
                borderRadius: "12px", 
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                overflow: "hidden",
                transition: "transform 0.2s ease",
                cursor: "pointer"
              }}
              onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
              onMouseLeave={(e) => e.target.style.transform = "translateY(0px)"}
            >
              <div style={{
                backgroundColor: "#dc3545",
                color: "white",
                padding: "1.5rem",
                textAlign: "center"
              }}>
                <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>🔴</div>
                <h2 style={{ margin: "0 0 0.5rem 0", fontSize: "1.5rem" }}>Google Malware Reporter</h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "0.9rem" }}>
                  Report malicious Android apps to Google Play Protect
                </p>
              </div>
              <div style={{ padding: "1.5rem" }}>
                {loading ? (
                  <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
                    Loading statistics...
                  </div>
                ) : (
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "1fr 1fr", 
                    gap: "0.75rem" 
                  }}>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#dc3545" }}>
                        {stats.googleMalware.totalReports}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Total Reports
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#ffc107" }}>
                        {stats.googleMalware.pendingReports}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Pending
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#28a745" }}>
                        {stats.googleMalware.submittedReports}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Submitted
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#17a2b8" }}>
                        {stats.googleMalware.todayReports}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Today
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/google-malware");
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      padding: "0.75rem",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      cursor: "pointer"
                    }}
                  >
                    🚨 Report App
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/google-malware/history");
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      padding: "0.75rem",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      cursor: "pointer"
                    }}
                  >
                    📊 View History
                  </button>
                </div>
              </div>
            </div>

            {/* Malware Bazaar Tool Card */}
            <div 
              style={{ 
                backgroundColor: "white", 
                borderRadius: "12px", 
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                overflow: "hidden",
                transition: "transform 0.2s ease",
                cursor: "pointer"
              }}
              onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
              onMouseLeave={(e) => e.target.style.transform = "translateY(0px)"}
            >
              <div style={{
                backgroundColor: "#9b59b6",
                color: "white",
                padding: "1.5rem",
                textAlign: "center"
              }}>
                <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>🦠</div>
                <h2 style={{ margin: "0 0 0.5rem 0", fontSize: "1.5rem" }}>Malware Bazaar</h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "0.9rem" }}>
                  Report malware samples to abuse.ch Malware Bazaar
                </p>
              </div>
              <div style={{ padding: "1.5rem" }}>
                {loading ? (
                  <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
                    Loading statistics...
                  </div>
                ) : (
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "1fr 1fr", 
                    gap: "0.75rem" 
                  }}>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#9b59b6" }}>
                        {stats.malwareBazaar.totalSubmissions}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Total Submissions
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#17a2b8" }}>
                        {stats.malwareBazaar.todaySubmissions}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Today
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#28a745" }}>
                        {stats.malwareBazaar.submitted}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Submitted
                      </div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#dc3545" }}>
                        {stats.malwareBazaar.failed}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Failed
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/malware-bazaar");
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "#9b59b6",
                      color: "white",
                      border: "none",
                      padding: "0.75rem",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      cursor: "pointer"
                    }}
                  >
                    📤 Upload Malware
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/malware-bazaar-history");
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      padding: "0.75rem",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      cursor: "pointer"
                    }}
                  >
                    📋 View History
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats Summary */}
          <div style={{ 
            backgroundColor: "white", 
            borderRadius: "8px", 
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            padding: "1.5rem"
          }}>
            <h3 style={{ margin: "0 0 1rem 0", color: "#333" }}>📊 Quick Overview</h3>
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", 
              gap: "1rem"
            }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#007bff" }}>
                  {loading ? "..." : stats.dmca.totalCases}
                </div>
                <div style={{ fontSize: "0.9rem", color: "#666" }}>Total DMCA Cases</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#17a2b8" }}>
                  {loading ? "..." : stats.dmca.todayCases}
                </div>
                <div style={{ fontSize: "0.9rem", color: "#666" }}>Today's Cases</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#28a745" }}>
                  {loading ? "..." : stats.spamhaus.totalSubmissions}
                </div>
                <div style={{ fontSize: "0.9rem", color: "#666" }}>Spamhaus Submissions</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#17a2b8" }}>
                  {loading ? "..." : stats.spamhaus.totalDomains}
                </div>
                <div style={{ fontSize: "0.9rem", color: "#666" }}>Domains Reported</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#dc3545" }}>
                  {loading ? "..." : stats.googleMalware.totalReports}
                </div>
                <div style={{ fontSize: "0.9rem", color: "#666" }}>Google Malware Reports</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ToolsDashboard;
